import React, { useState, useEffect } from 'react';
import { Drawer, Spin, Tag, Switch, Modal } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useAuth } from '../api/AuthContext.js';
import { deleteParticipant } from '../api/deleteParticipant.js';
import { fetchParticipant } from '../api/fetchParticipant.js';
import { updateParticipant } from '../api/updateParticipant.js';
import { fetchAllEventsByOwner } from '../api/fetchAllEvents.js';
import { fetchAllowedParticipants } from '../api/fetchParticipants.js';
import { toggleAllowedAttendance } from '../api/participantEvents.js';
import ParticipantAttendanceModal from '../components/ParticipantAttendanceModal.js';
import ProfileImage from '../components/ProfileImage.js';
import { showToast, updateToast } from '../utils/toastUtils.js';
import * as utils from '../utils/utils.js';

export default function ParticipantOverview({
    participant,
    refreshParticipants,
}) {
    const auth = useAuth();

    // Basic Participant properties.
    const [participantName, setParticipantName] = useState('');
    const [participantNickname, setParticipantNickName] = useState('');
    const [participantEmail, setParticipantEmail] = useState('');
    const [participantPhone, setParticipantPhone] = useState('');
    const [participantSsn, setParticipantSsn] = useState('');
    const [participantActive, setParticipantActive] = useState(false);
    const [participantPicture, setParticipantPicture] = useState('');
    // Event subscriptions
    const [participantEvents, setParticipantEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [togglingEventId, setTogglingEventId] = useState(null);
    const [togglingAllEvents, setTogglingAllEvents] = useState(false);
    // Flow control.
    const [isShowingProfile, setIsShowingProfile] = useState(false);
    const [isShowingEditParticipantDrawer, setIsShowingEditParticipantDrawer] =
        useState(false);
    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] =
        useState(false);

    const getParticipant = async () => {
        try {
            const res = await fetchParticipant(participant.participant_id);
            setParticipantName(res.name);
            setParticipantNickName(res.nickname);
            setParticipantEmail(res.email);
            setParticipantPhone(res.mobile_number);
            setParticipantSsn(res.ssn);
            setParticipantActive(res.active);
            setParticipantPicture(res.image_url);
        } catch (err) {
            console.error(err);
        }
    };

    const getParticipantEvents = async () => {
        setLoadingEvents(true);
        try {
            // Fetch all events by owner
            const events = await fetchAllEventsByOwner(participant.owner_id);

            // For each event, fetch allowed participants to check if this participant is allowed
            const eventsWithAllowedStatus = await Promise.all(
                events.map(async (event) => {
                    try {
                        const allowedParticipants = await fetchAllowedParticipants(event.event_id);
                        return {
                            ...event,
                            allowed_participants: allowedParticipants || [],
                        };
                    } catch (err) {
                        console.error(`Error fetching allowed participants for event ${event.event_id}:`, err);
                        return {
                            ...event,
                            allowed_participants: [],
                        };
                    }
                })
            );

            setParticipantEvents(eventsWithAllowedStatus || []);
        } catch (err) {
            console.error('Error fetching participant events:', err);
            setParticipantEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    };

    const commitDeleteParticipant = async (participantId) => {
        const toastId = showToast('One moment...', auth.currentUser.email, {
            toastId: 'toastIdDeleteParticipant',
        });

        try {
            await deleteParticipant(participantId);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Participant deleted',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshParticipants();
            setIsShowingDeleteConfirmation(false);
        } catch (err) {
            console.error(err);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Something went wrong',
                type: 'error',
                autoClose: 2000,
                isLoading: false,
            });
        }
    };

    const commitEditParticipant = async () => {
        const toastId = showToast('One moment...', auth.currentUser.email, {
            toastId: 'toastIdUpdateParticipant',
        });

        try {
            const birthdate = utils.obtainBirthdateFromSsn(participantSsn);

            await updateParticipant(
                participant.participant_id,
                auth.currentUser.user_id,
                participantName,
                participantNickname,
                participantEmail,
                participantPhone,
                participantSsn,
                birthdate || null,
                participantActive
            );
            updateToast(toastId, auth.currentUser.email, {
                render: 'Participant updated',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshParticipants();
            setIsShowingEditParticipantDrawer(false);
        } catch (err) {
            console.error(err);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Something went wrong',
                type: 'error',
                autoClose: 2000,
                isLoading: false,
            });
        }
    };

    const handleActiveChange = async () => {
        const newActiveState = !participantActive;
        setParticipantActive(newActiveState);

        // Sync all event permissions with the new active state
        if (participantEvents.length > 0) {
            const toastId = showToast(
                newActiveState
                    ? 'Adding participant to all events...'
                    : 'Removing participant from all events...',
                auth.currentUser.email,
                { toastId: 'toastIdSyncAllEvents' }
            );

            try {
                // Toggle each event's allowed participant status
                for (const event of participantEvents) {
                    const isCurrentlyAllowed = event.allowed_participants?.some(
                        ap => ap.participant_id === participant.participant_id
                    );

                    // Only toggle if the current state doesn't match the desired state
                    if (newActiveState && !isCurrentlyAllowed) {
                        await toggleAllowedAttendance(event.event_id, participant.participant_id);
                    } else if (!newActiveState && isCurrentlyAllowed) {
                        await toggleAllowedAttendance(event.event_id, participant.participant_id);
                    }
                }

                // Refresh events to show updated state
                await getParticipantEvents();

                updateToast(toastId, auth.currentUser.email, {
                    render: newActiveState
                        ? 'Participant added to all events'
                        : 'Participant removed from all events',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
            } catch (err) {
                console.error('Error syncing event permissions:', err);
                updateToast(toastId, auth.currentUser.email, {
                    render: 'Error updating event permissions',
                    type: 'error',
                    autoClose: 2000,
                    isLoading: false,
                });
            }
        }
    };

    const handleEventToggle = async (eventId) => {
        setTogglingEventId(eventId);
        try {
            await toggleAllowedAttendance(eventId, participant.participant_id);
            // Refresh events to show updated state
            await getParticipantEvents();
        } catch (err) {
            console.error('Error toggling event permission:', err);
            showToast('Error updating event permission', auth.currentUser.email, {
                type: 'error',
                autoClose: 2000,
            });
        } finally {
            setTogglingEventId(null);
        }
    };

    const handleToggleAllEvents = async (checked) => {
        setTogglingAllEvents(true);

        const toastId = showToast(
            checked
                ? 'Adding participant to all events...'
                : 'Removing participant from all events...',
            auth.currentUser.email,
            { toastId: 'toastIdToggleAllEvents' }
        );

        try {
            // Toggle each event's allowed participant status
            for (const event of participantEvents) {
                const isCurrentlyAllowed = event.allowed_participants?.some(
                    ap => ap.participant_id === participant.participant_id
                );

                // Only toggle if the current state doesn't match the desired state
                if (checked && !isCurrentlyAllowed) {
                    await toggleAllowedAttendance(event.event_id, participant.participant_id);
                } else if (!checked && isCurrentlyAllowed) {
                    await toggleAllowedAttendance(event.event_id, participant.participant_id);
                }
            }

            // Refresh events to show updated state
            await getParticipantEvents();

            updateToast(toastId, auth.currentUser.email, {
                render: checked
                    ? 'Participant added to all events'
                    : 'Participant removed from all events',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
        } catch (err) {
            console.error('Error toggling all event permissions:', err);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Error updating event permissions',
                type: 'error',
                autoClose: 2000,
                isLoading: false,
            });
        } finally {
            setTogglingAllEvents(false);
        }
    };

    const toggleEditParticipantDrawer = () => {
        if (!isShowingEditParticipantDrawer) {
            getParticipant();
            getParticipantEvents();
        }
        setIsShowingEditParticipantDrawer(!isShowingEditParticipantDrawer);
    };

    const toggleParticipantProfileModal = () => {
        getParticipant();
        setIsShowingProfile(!isShowingProfile);
    };

    return (
        <>
            <tr
                key={'tr-' + participant.participant_id}
                style={{
                    borderBottom: '1px solid #e8e8e8',
                    transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
                <td style={{ padding: '3px 8px', width: '40px', verticalAlign: 'middle' }}>
                    <div style={{ width: '26px', height: '26px' }}>
                        <ProfileImage participant={participant} size={26} />
                    </div>
                </td>
                <td style={{ padding: '3px 8px', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span
                            id={'viewParticipant-' + participant.participant_id}
                            className="name"
                            onClick={() => toggleParticipantProfileModal()}
                            style={{
                                cursor: 'pointer',
                                fontWeight: '500',
                                color: '#1f2937',
                                fontSize: '12px'
                            }}
                        >
                            {participant.name}
                        </span>
                        {participant.nickname && (
                            <span style={{
                                color: '#6b7280',
                                fontSize: '11px'
                            }}>
                                "{participant.nickname}"
                            </span>
                        )}
                        {participant.email && (
                            <span style={{
                                color: '#9ca3af',
                                fontSize: '10px'
                            }}>
                                {participant.email}
                            </span>
                        )}
                    </div>
                </td>
                <td style={{ padding: '3px 8px', width: '70px', textAlign: 'center', verticalAlign: 'middle' }}>
                    {participant.active ? (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 6px',
                            borderRadius: '8px',
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            fontSize: '10px',
                            fontWeight: '500'
                        }}>
                            Active
                        </span>
                    ) : (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 6px',
                            borderRadius: '8px',
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            fontSize: '10px',
                            fontWeight: '500'
                        }}>
                            Inactive
                        </span>
                    )}
                </td>
                <td style={{ padding: '3px 8px', width: '80px', textAlign: 'right', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', gap: '3px', justifyContent: 'flex-end' }}>
                        <button
                            id={'editParticipant-' + participant.participant_id}
                            onClick={() => toggleEditParticipantDrawer()}
                            style={{
                                background: 'none',
                                border: '1px solid #d1d5db',
                                borderRadius: '3px',
                                padding: '2px 6px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                color: '#374151',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                e.currentTarget.style.borderColor = '#9ca3af';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                        >
                            Edit
                        </button>
                        <button
                            id={'deleteParticipant-' + participant.participant_id}
                            onClick={() => setIsShowingDeleteConfirmation(true)}
                            style={{
                                background: 'none',
                                border: '1px solid #fecaca',
                                borderRadius: '3px',
                                padding: '2px 6px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                color: '#dc2626',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#fef2f2';
                                e.currentTarget.style.borderColor = '#f87171';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#fecaca';
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </td>
            </tr>

            {isShowingDeleteConfirmation && (
                <>
                    <style>
                        {`
                            .modal-card-head .delete {
                                flex-shrink: 0 !important;
                                flex-grow: 0 !important;
                            }
                            .modal-card-head .delete::before {
                                background-color: #363636 !important;
                                height: 1.5px !important;
                                width: 25% !important;
                                transform: translateX(-50%) translateY(-50%) rotate(45deg) !important;
                            }
                            .modal-card-head .delete::after {
                                background-color: #363636 !important;
                                height: 1.5px !important;
                                width: 25% !important;
                                transform: translateX(-50%) translateY(-50%) rotate(-45deg) !important;
                            }
                        `}
                    </style>
                    <div className="modal is-active">
                        <div className="modal-background"></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">
                                    Delete participant: {participant.name}?
                                </p>
                                <button
                                    className="delete"
                                    aria-label="close"
                                    type="button"
                                    onClick={() =>
                                        setIsShowingDeleteConfirmation(false)
                                    }
                                ></button>
                            </header>
                        <section className="modal-card-body">
                            Are you sure? This will delete the participant, and
                            their associated data. This can not be undone!
                            <div style={{ marginTop: '12px' }}>
                                Note: The deletion does not affect Attendance
                                records for the participant (to preserve
                                accuracy of statistics) and Chats written by the
                                participant will also be retained (to preserve
                                chat conversations in Event).
                            </div>
                        </section>
                        <footer className="modal-card-foot">
                            <button
                                id="confirmDeleteParticipant"
                                className="button is-danger"
                                onClick={() =>
                                    commitDeleteParticipant(
                                        participant.participant_id
                                    )
                                }
                            >
                                Confirm delete
                            </button>
                            <button
                                id="cancelDeleteParticipant"
                                className="button"
                                onClick={() =>
                                    setIsShowingDeleteConfirmation(
                                        !isShowingDeleteConfirmation
                                    )
                                }
                            >
                                Cancel
                            </button>
                        </footer>
                    </div>
                </div>
                </>
            )}
            <Drawer
                title="Edit Participant"
                placement="right"
                onClose={toggleEditParticipantDrawer}
                open={isShowingEditParticipantDrawer}
                width={500}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <button
                            id="cancelEditParticipant"
                            className="button"
                            onClick={toggleEditParticipantDrawer}
                            style={{ marginRight: '8px' }}
                        >
                            Cancel
                        </button>
                        <button
                            id="confirmEditParticipant"
                            className="button is-success"
                            onClick={commitEditParticipant}
                        >
                            Update
                        </button>
                    </div>
                }
            >
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                        Participant Details
                    </h3>
                    <div className="field">
                        <div className="activeSwitch">
                            <input
                                id="active"
                                type="checkbox"
                                name="active"
                                checked={participantActive}
                                onChange={handleActiveChange}
                                className="switch is-rounded is-success"
                            />
                            <label htmlFor="active">Active</label>
                        </div>
                    </div>
                    <div className="field">
                        <label className="label">Name</label>
                        <input
                            className="input"
                            type="text"
                            id="name"
                            placeholder="Enter name"
                            value={participantName}
                            onChange={(e) =>
                                setParticipantName(e.target.value)
                            }
                        />
                    </div>
                    <div className="field">
                        <label className="label">Nickname</label>
                        <input
                            className="input"
                            type="text"
                            id="nickname"
                            placeholder="Enter nick name"
                            value={participantNickname}
                            onChange={(e) =>
                                setParticipantNickName(e.target.value)
                            }
                        />
                    </div>
                    <div className="field">
                        <label className="label">Email</label>
                        <input
                            className="input"
                            type="email"
                            id="email"
                            placeholder="Enter email"
                            value={participantEmail}
                            onChange={(e) =>
                                setParticipantEmail(e.target.value)
                            }
                        />
                    </div>
                    <div className="field">
                        <label className="label">Phone</label>
                        <input
                            className="input"
                            type="tel"
                            id="phone"
                            placeholder="Enter mobile phone number"
                            value={participantPhone}
                            onChange={(e) =>
                                setParticipantPhone(e.target.value)
                            }
                        />
                    </div>
                    <div className="field">
                        <label className="label">SSN / Kennitala</label>
                        <input
                            className="input"
                            type="number"
                            id="ssn"
                            placeholder="Enter social security number"
                            value={participantSsn}
                            onChange={(e) =>
                                setParticipantSsn(e.target.value)
                            }
                        />
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: '16px' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                    }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
                            Event Subscriptions
                        </h3>
                        {!loadingEvents && participantEvents.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#666' }}>
                                    Toggle All
                                </span>
                                <Switch
                                    checked={participantEvents.every(event =>
                                        event.allowed_participants?.some(
                                            ap => ap.participant_id === participant.participant_id
                                        )
                                    )}
                                    loading={togglingAllEvents}
                                    onChange={handleToggleAllEvents}
                                    size="small"
                                />
                            </div>
                        )}
                    </div>
                    {loadingEvents ? (
                        <div style={{ textAlign: 'center', padding: '12px' }}>
                            <Spin />
                        </div>
                    ) : participantEvents.length === 0 ? (
                        <p style={{ color: '#999', fontStyle: 'italic', fontSize: '13px' }}>
                            This participant is not subscribed to any events.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {participantEvents.map((event) => {
                                const isAllowed = event.allowed_participants?.some(
                                    ap => ap.participant_id === participant.participant_id
                                );
                                return (
                                    <div
                                        key={event.event_id}
                                        style={{
                                            padding: '8px 10px',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '4px',
                                            backgroundColor: '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontWeight: '600',
                                                fontSize: '13px',
                                                marginBottom: '2px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {event.name}
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                {event.type === 0 ? (
                                                    <Tag color="blue" style={{ fontSize: '11px', margin: 0, padding: '0 4px', lineHeight: '18px' }}>
                                                        Recurring
                                                    </Tag>
                                                ) : (
                                                    <Tag color="green" style={{ fontSize: '11px', margin: 0, padding: '0 4px', lineHeight: '18px' }}>
                                                        One-time
                                                    </Tag>
                                                )}
                                                {event.is_paused && (
                                                    <Tag color="orange" style={{ fontSize: '11px', margin: 0, padding: '0 4px', lineHeight: '18px' }}>
                                                        Paused
                                                    </Tag>
                                                )}
                                                {event.sessions && event.sessions.length > 0 && (
                                                    <span style={{ fontSize: '11px', color: '#666' }}>
                                                        Next: {dayjs(event.sessions[0].timestamp).format('MMM D')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <Switch
                                            checked={isAllowed}
                                            loading={togglingEventId === event.event_id}
                                            onChange={() => handleEventToggle(event.event_id)}
                                            size="small"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Drawer>
            <Modal
                open={isShowingProfile}
                onCancel={toggleParticipantProfileModal}
                footer={null}
                width={500}
                closeIcon={<CloseOutlined />}
                styles={{
                    body: { padding: 0 }
                }}
            >
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '40px 24px 24px',
                    textAlign: 'center',
                    borderRadius: '8px 8px 0 0'
                }}>
                    <div style={{
                        width: '100px',
                        height: '100px',
                        margin: '0 auto 16px',
                        border: '4px solid white',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <ProfileImage participant={participant} size={92} />
                    </div>
                    <h2 id="participantOverviewName" style={{
                        color: '#fff',
                        fontSize: '24px',
                        fontWeight: '600',
                        margin: '0 0 8px 0'
                    }}>
                        {participantName}
                    </h2>
                    {participantNickname && (
                        <p id="participantOverviewNickname" style={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '16px',
                            margin: '0'
                        }}>
                            "{participantNickname}"
                        </p>
                    )}
                </div>

                <div style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '16px'
                        }}>
                            Contact Information
                        </h3>

                        {participantEmail && (
                            <div id="participantOverviewEmail" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                marginBottom: '8px'
                            }}>
                                <MailOutlined style={{ fontSize: '18px', color: '#667eea' }} />
                                <div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Email</div>
                                    <div style={{ fontSize: '14px', color: '#1f2937' }}>{participantEmail}</div>
                                </div>
                            </div>
                        )}

                        {participantPhone && (
                            <div id="participantOverviewPhone" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                marginBottom: '8px'
                            }}>
                                <PhoneOutlined style={{ fontSize: '18px', color: '#667eea' }} />
                                <div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Phone</div>
                                    <div style={{ fontSize: '14px', color: '#1f2937' }}>{participantPhone}</div>
                                </div>
                            </div>
                        )}

                        {participantSsn && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px'
                            }}>
                                <IdcardOutlined style={{ fontSize: '18px', color: '#667eea' }} />
                                <div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>ID / SSN</div>
                                    <div style={{ fontSize: '14px', color: '#1f2937' }}>{participantSsn}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <ParticipantAttendanceModal
                        key={'modal-' + participant.participant_id}
                        participant_id={participant.participant_id}
                        participantName={participant.name}
                        participant_owner_id={participant.owner_id}
                    />
                </div>
            </Modal>
        </>
    );
}
